import React from 'react';
import { string, func, bool } from 'prop-types';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, intlShape, injectIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { propTypes } from '../../util/types';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { defaultVatPercentage } from '../../util/misc';
import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
    </div>
  );
};

export const ListingCardComponent = props => {
  const config = useConfiguration();
  const {
    className,
    rootClassName,
    intl,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const { noPrice, availableDate, notAvailableDate } = publicData || {};

  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorPublicData = author.attributes.profile?.publicData || {};

  const authorName =
    authorPublicData?.customDisplayName || author.attributes.profile.displayName;

  const { isUsingSpecialRentalPricing, specialRentalPricingLabel } = authorPublicData
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
      onMouseEnter: () => setActiveListing(currentListing.id),
      onMouseLeave: () => setActiveListing(null),
    }
    : null;

  const address = publicData?.location?.secondaryText || publicData?.location?.address;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const isSaleFlow = publicData?.listingType.includes('for-sale');

  const vat = publicData?.vat;
  const vatPercentage = vat && vat !== 'no-vat' ? defaultVatPercentage : null;

  const getPerDaysRentLabel = () => {
    if (specialRentalPricingLabel) {
      return specialRentalPricingLabel;
    } else {
      return "ListingCard.specialPriceForRent";
    };
  };

  return (
    <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <AspectRatioWrapper
        className={css.aspectRatioWrapper}
        width={aspectWidth}
        height={aspectHeight}
        {...setActivePropsMaybe}
      >
        <LazyImage
          rootClassName={css.rootForImage}
          alt={title}
          image={firstImage}
          variants={variants}
          sizes={renderSizes}
        />
      </AspectRatioWrapper>
      <div className={css.info}>
        <div className={css.contentTextCard}>
          <div className={css.displayContent}>
            <div className={css.title}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </div>
            {showAuthorInfo ? (
              <div className={css.authorInfo}>
                <FormattedMessage id="ListingCard.author" values={{ authorName }} />
              </div>
            ) : null}
            {address ? <div className={css.authorInfo}>{address}</div> : null}
          </div>

          {price && !noPrice ? (
            <div className={css.rightFlex}>
              <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
              <div className={css.vatColor}>
                <FormattedMessage id="ListingCard.plusVat" />
                {vatPercentage ? vatPercentage : 0}%
              </div>
              {isBookable ? (
                <div className={css.subTitleUpperCase}>
                  <FormattedMessage
                    id={
                      isSaleFlow
                        ? "ListingCard.perUnit"
                        : getPerDaysRentLabel()
                    }
                    values={{ unitType: publicData?.unitType }}
                  />
                </div>
              ) : null}
              <div className={css.subTitleUpperCaseRentSale}>
                <FormattedMessage
                  id={
                    isSaleFlow
                      ? 'ListingCard.forSale'
                      : 'ListingCard.forRent'
                  }
                />
              </div>
            </div>
          ) : (
            <div className={css.subTitleUpperCase}>
              <FormattedMessage id="ListingCard.contacForPricing" />
            </div>
          )}
        </div>
        <div className={css.dateStyle} style={{ marginTop: '9px' }}>
          {!!availableDate ? <>
            <FormattedMessage id="ListingCard.availableFrom" />
            {availableDate
              .slice(0, 10)
              .split('-')
              .reverse()
              .join('.')}
          </> : null}
        </div>
        <div className={css.dateStyle}>
          {!!notAvailableDate ? <>
            <FormattedMessage id="ListingCard.notAvailableAfter" />
            {notAvailableDate
              .slice(0, 10)
              .split('-')
              .reverse()
              .join('.')}
          </> : null}
        </div>
      </div>
    </NamedLink>
  );
};

ListingCardComponent.defaultProps = {
  className: null,
  rootClassName: null,
  renderSizes: null,
  setActiveListing: null,
  showAuthorInfo: true,
};

ListingCardComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  listing: propTypes.listing.isRequired,
  showAuthorInfo: bool,

  // Responsive image sizes hint
  renderSizes: string,

  setActiveListing: func,
};

export default injectIntl(ListingCardComponent);
